import {
  DOCUMENT,
  Injectable,
  computed,
  inject,
  signal,
} from '@angular/core';
import * as Sentry from '@sentry/angular';

/**
 * Tipo `BeforeInstallPromptEvent` não está nas DOM lib types padrão (é
 * proposta WICG, suporte amplo em Chromium-based browsers + Edge).
 * Definimos inline.
 */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: ReadonlyArray<string>;
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

const DISMISS_KEY = 'qa-pwa-install-dismissed-until';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 dias

/**
 * InstallPromptService — orquestra o prompt nativo de install PWA.
 *
 * Fluxo:
 *   1. No boot, capturamos `beforeinstallprompt` (browser dispatch quando
 *      app atende critério: manifest válido + SW + interação do usuário).
 *   2. Não oferecemos imediatamente — usuário precisa concluir onboarding
 *      + esperar 30s. Heurística: usuário engajado o suficiente pra
 *      tomar a decisão consciente, não-engajado nem vê o modal.
 *   3. Se aceita → `event.prompt()` mostra o nativo do browser.
 *   4. Se dismiss → lembramos por 7 dias (localStorage) — não reaparece.
 *
 * iOS Safari NÃO dispara `beforeinstallprompt` (limitação Apple). Pra iOS
 * vamos precisar de modal informativo separado em Sprint pós-MVP. Por
 * ora, em iOS, `shouldOffer()` fica `false` → nada aparece (degradado, OK).
 */
@Injectable({ providedIn: 'root' })
export class InstallPromptService {
  private readonly document = inject(DOCUMENT);

  private readonly stashedEvent = signal<BeforeInstallPromptEvent | null>(null);
  private readonly userMarkedReady = signal(false);
  private readonly dismissedUntil = signal(0);
  private readonly visible = signal(false);

  readonly shouldOffer = computed(
    () =>
      this.stashedEvent() !== null &&
      this.userMarkedReady() &&
      Date.now() > this.dismissedUntil() &&
      !this.isInstalled() &&
      this.visible(),
  );

  constructor() {
    const win = this.document.defaultView;
    if (!win) return; // SSR safety; não aplicável neste app mas defensivo.

    win.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      this.stashedEvent.set(e as BeforeInstallPromptEvent);
      Sentry.addBreadcrumb({
        category: 'pwa-install',
        type: 'info',
        message: 'beforeinstallprompt capturado — install disponível',
      });
    });

    win.addEventListener('appinstalled', () => {
      this.stashedEvent.set(null);
      this.visible.set(false);
      Sentry.addBreadcrumb({
        category: 'pwa-install',
        type: 'info',
        message: 'PWA instalado',
      });
    });

    // Hidrata estado de dismissal do localStorage (7d window).
    try {
      const raw = win.localStorage.getItem(DISMISS_KEY);
      if (raw) {
        const until = Number(raw);
        if (Number.isFinite(until) && until > Date.now()) {
          this.dismissedUntil.set(until);
        }
      }
    } catch {
      // localStorage pode estar bloqueado (Safari modo privado etc.) — ok.
    }
  }

  /**
   * Chamado pelo OnboardingWizard após aceite do ToS — agenda exibição
   * do prompt em 30s. O modal só aparece se `shouldOffer()` for true
   * naquele momento (event capturado, não dismissido, não instalado).
   */
  scheduleOffer(delayMs = 30_000): void {
    const win = this.document.defaultView;
    if (!win) return;

    this.userMarkedReady.set(true);

    win.setTimeout(() => {
      if (this.stashedEvent() && !this.isInstalled()) {
        this.visible.set(true);
      }
    }, delayMs);
  }

  /**
   * Mostra o prompt nativo. Resolve com `'accepted' | 'dismissed'`
   * conforme escolha do usuário.
   */
  async presentNativePrompt(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    const event = this.stashedEvent();
    if (!event) return 'unavailable';

    this.visible.set(false);
    await event.prompt();
    const choice = await event.userChoice;
    this.stashedEvent.set(null);

    Sentry.addBreadcrumb({
      category: 'pwa-install',
      type: 'info',
      message: `Usuário ${choice.outcome === 'accepted' ? 'aceitou' : 'recusou'} install`,
      data: { outcome: choice.outcome, platform: choice.platform },
    });

    if (choice.outcome === 'dismissed') {
      this.persistDismissal();
    }

    return choice.outcome;
  }

  /**
   * Usuário clicou "Agora não" no modal customizado — não chamamos o
   * prompt nativo, apenas registramos que vai ficar quieto por 7 dias.
   */
  dismiss(): void {
    this.visible.set(false);
    this.persistDismissal();
    Sentry.addBreadcrumb({
      category: 'pwa-install',
      type: 'info',
      message: 'Usuário dismissou modal customizado (sem ver prompt nativo)',
    });
  }

  private persistDismissal(): void {
    const until = Date.now() + DISMISS_DURATION_MS;
    this.dismissedUntil.set(until);
    try {
      this.document.defaultView?.localStorage.setItem(
        DISMISS_KEY,
        String(until),
      );
    } catch {
      // localStorage indisponível — dismissal vira só pra esta sessão.
    }
  }

  private isInstalled(): boolean {
    const win = this.document.defaultView;
    if (!win) return false;
    return win.matchMedia('(display-mode: standalone)').matches;
  }
}
